import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, Card, CardContent, Typography, IconButton, Tooltip } from "@mui/material";
import { Plus, Trash2, Edit } from "lucide-react";
import { useSelector } from "react-redux";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import ChartBuilder from "./ChartBuilder";
import ChartDisplay from "./ChartDisplay";

const ChartsPage = ({ statsPageId }) => {
  const [charts, setCharts] = useState([]);
  const [pageTitle, setPageTitle] = useState("");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingChart, setEditingChart] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const currentInternalUser = useSelector((state) => state.current_internal_user);
  const canManage = ["coordinator", "admin"].includes(currentInternalUser?.role);

  const layout = useMemo(() =>
    charts.map((chart, index) => ({
      i: String(chart.id),
      x: chart.display_config?.layout?.x ?? (index % 2) * 6,
      y: chart.display_config?.layout?.y ?? Math.floor(index / 2) * 3,
      w: chart.display_config?.layout?.w ?? (chart.chart_type === "kpi" ? 3 : 6),
      h: chart.display_config?.layout?.h ?? (chart.chart_type === "kpi" ? 1 : 3),
      minW: chart.chart_type === "kpi" ? 2 : 3,
      minH: chart.chart_type === "kpi" ? 1 : 2,
      resizeHandles: editMode ? ["sw", "nw", "se", "ne"] : [],
    })), [charts, editMode]);

  const fetchCharts = () => {
    sh.get("/internal/saved_charts", { params: { stats_page_id: statsPageId } })
      .then((res) => setCharts(res.data))
      .catch(() => enqueueSnackbar("Failed to load charts", { variant: "error" }));
  };

  useEffect(() => {
    if (!statsPageId) return;
    sh.get(`/internal/stats_pages/${statsPageId}`)
      .then((res) => setPageTitle(res.data.title))
      .catch(() => enqueueSnackbar("Failed to load page", { variant: "error" }));
    fetchCharts();
  }, [statsPageId]);

  const handleDelete = (id) => {
    if (!confirm("Delete this chart?")) return;
    sh.delete(`/internal/saved_charts/${id}`)
      .then(() => {
        enqueueSnackbar("Chart deleted", { variant: "success" });
        fetchCharts();
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          enqueueSnackbar("Only coordinators and admins can delete charts", { variant: "error" });
          return;
        }
        enqueueSnackbar("Failed to delete chart", { variant: "error" });
      });
  };

  const handleSave = (savedChart) => {
    setBuilderOpen(false);
    if (editingChart) {
      setCharts((prev) => prev.map((c) => (c.id === savedChart.id ? { ...savedChart, display_config: { ...savedChart.display_config, layout: c.display_config?.layout } } : c)));
    } else {
      fetchCharts();
    }
    setEditingChart(null);
  };

  const handleDragOrResizeStop = (newLayout) => {
    if (!editMode) return;

    const updatedCharts = charts.map(chart => {
      const item = newLayout.find(l => String(chart.id) === l.i);
      if (item) {
        return { ...chart, display_config: { ...chart.display_config, layout: { x: item.x, y: item.y, w: item.w, h: item.h } } };
      }
      return chart;
    });
    setCharts(updatedCharts);

    newLayout.forEach((item) => {
      const chart = charts.find(c => String(c.id) === item.i);
      if (chart) {
        const currentLayout = chart.display_config?.layout;
        if (!currentLayout || currentLayout.x !== item.x || currentLayout.y !== item.y ||
            currentLayout.w !== item.w || currentLayout.h !== item.h) {
          sh.put(`/internal/saved_charts/${chart.id}`, {
            saved_chart: { display_config: { ...chart.display_config, layout: { x: item.x, y: item.y, w: item.w, h: item.h } } }
          }).catch((err) => {
            console.error("Failed to save layout:", err);
            if (err?.response?.status === 401) {
              enqueueSnackbar("Only coordinators and admins can edit charts", { variant: "error" });
            } else {
              enqueueSnackbar("Failed to save layout", { variant: "error" });
            }
            fetchCharts();
          });
        }
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <style>{`
        .react-grid-item.react-draggable-dragging {
          cursor: grabbing !important;
        }
        .react-grid-item.cssTransforms {
          cursor: ${editMode ? "grab" : "default"};
        }
        .react-resizable-handle {
          cursor: nwse-resize !important;
        }
      `}</style>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
          {pageTitle}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={canManage ? "" : "Coordinator or admin required"} arrow>
            <span>
              <Button
                variant="outlined"
                size="small"
                disableRipple
                disableElevation
                disabled={!canManage}
                onClick={() => setEditMode(!editMode)}
                sx={{
                  textTransform: "none",
                  height: "40px",
                  px: 1.5,
                  borderRadius: "calc(-2px + 0.75rem)",
                  border: "1px solid hsl(140 8% 88%)",
                  backgroundColor: "#ffffff",
                  color: "hsl(150 38% 17%)",
                  fontSize: 14,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  transition: "opacity 0.15s ease",
                  "&:hover": {
                    backgroundColor: "#ffffff",
                    borderColor: "hsl(140 8% 88%)",
                    opacity: 0.9,
                  }
                }}
              >
                {editMode ? "Done" : "Edit Layout"}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            disableRipple
            disableElevation
            startIcon={<Plus size={16} />}
            onClick={() => setBuilderOpen(true)}
            sx={{
              textTransform: "none",
              height: "40px",
              px: 1.5,
              borderRadius: "calc(-2px + 0.75rem)",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              "& .MuiButton-startIcon": { mr: 0.75 },
              transition: "opacity 0.15s ease",
              "&:hover": {
                backgroundColor: "hsl(150 38% 17%)",
                opacity: 0.9,
              }
            }}
          >
            New Chart
          </Button>
        </Box>
      </Box>

      <Box sx={{ minHeight: "calc(100vh - 200px)", position: "relative" }}>
        <GridLayout
          className="layout"
          layout={layout}
          cols={12}
          rowHeight={100}
          width={1200}
          isDraggable={editMode}
          isResizable={editMode}
          compactType={null}
          preventCollision={false}
          onDragStop={handleDragOrResizeStop}
          onResizeStop={handleDragOrResizeStop}
        >
          {charts.map((chart) => (
            <div key={String(chart.id)}>
              {chart.chart_type === "kpi" ? (
                <Box sx={{ height: "100%", position: "relative" }}>
                  {editMode && (
                    <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, display: "flex", gap: 0.5 }}>
                      <IconButton size="small" onClick={() => { setEditingChart(chart); setBuilderOpen(true); }} sx={{ backgroundColor: "rgba(255,255,255,0.9)" }}>
                        <Edit size={16} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(chart.id)} sx={{ backgroundColor: "rgba(255,255,255,0.9)" }}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Box>
                  )}
                  <ChartDisplay chart={chart} key={`${chart.id}-${chart.updated_at}`} />
                </Box>
              ) : (
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "0.75rem",
                    border: "1px solid hsl(140 8% 88%)",
                    backgroundColor: "#ffffff",
                    overflow: "hidden",
                    transition: "box-shadow 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 1px 4px rgba(16,24,40,0.08)",
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: "1.25rem", "&:last-child": { pb: "1.25rem" } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography sx={{
                        fontSize: 14,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                        letterSpacing: -0.1,
                      }}>
                        {chart.title}
                      </Typography>
                      {editMode && (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton size="small" onClick={() => { setEditingChart(chart); setBuilderOpen(true); }} sx={{ backgroundColor: "rgba(255,255,255,0.9)", "&:hover": { backgroundColor: "hsl(140 8% 95%)" } }}>
                            <Edit size={14} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(chart.id)} sx={{ backgroundColor: "rgba(255,255,255,0.9)", "&:hover": { backgroundColor: "hsl(0 80% 96%)" } }}>
                            <Trash2 size={14} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <ChartDisplay
                        chart={chart}
                        key={`${chart.id}-${chart.display_config?.layout?.w}-${chart.display_config?.layout?.h}-${chart.updated_at}`}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </div>
          ))}
        </GridLayout>
      </Box>

      <ChartBuilder
        open={builderOpen}
        chart={editingChart}
        charts={charts}
        statsPageId={statsPageId}
        onClose={() => { setBuilderOpen(false); setEditingChart(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

export default ChartsPage;
